import axios from 'axios'
import {
  AdminMeeting,
  AdminUser,
  AccountOnboardingStatus,
  MeetingStats,
  MeetingsQueryResponse,
  MeetingType,
  MeetingStatus
} from './_models'
import {getAdminToken} from '../../auth/_requests'

const API_URL = process.env.REACT_APP_API_URL

// Create axios instance with credentials
const adminApi = axios.create({
  withCredentials: true
})

// Add request interceptor to include admin token
adminApi.interceptors.request.use((config) => {
  const token = getAdminToken()
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ============================================
// MEETING ENDPOINTS
// ============================================

// Get meeting stats for bubble
export function getMeetingStats(): Promise<{data: MeetingStats}> {
  return adminApi.get(`${API_URL}/admin/meetings/stats`)
}

// Get today's meetings for bubble panel
export function getTodayMeetings(): Promise<{data: {meetings: AdminMeeting[]}}> {
  return adminApi.get(`${API_URL}/admin/meetings/today`)
}

// Get admin users for assignment dropdown
export function getAdminUsers(): Promise<{data: {admins: AdminUser[]}}> {
  return adminApi.get(`${API_URL}/admin/meetings/admins/list`)
}

// Get meetings list with filters
export function getMeetings(params: {
  status?: MeetingStatus | 'all'
  assigned_to?: string
  meeting_type?: MeetingType | 'all'
  account?: string
  date_from?: string
  date_to?: string
  search?: string
  page?: number
  limit?: number
  sort?: string
}): Promise<{data: MeetingsQueryResponse}> {
  return adminApi.get(`${API_URL}/admin/meetings`, {params})
}

// Get single meeting
export function getMeeting(id: string): Promise<{data: {meeting: AdminMeeting}}> {
  return adminApi.get(`${API_URL}/admin/meetings/${id}`)
}

// Create meeting
export function createMeeting(data: {
  account: string
  assigned_to: string
  meeting_date: string
  meeting_type?: MeetingType
  title: string
  notes?: string
}): Promise<{data: {success: boolean; meeting: AdminMeeting}}> {
  return adminApi.post(`${API_URL}/admin/meetings`, data)
}

// Update meeting
export function updateMeeting(
  id: string,
  data: {
    account?: string
    assigned_to?: string
    meeting_date?: string
    meeting_type?: MeetingType
    title?: string
    notes?: string
    summary?: string
    status?: MeetingStatus
  }
): Promise<{data: {success: boolean; meeting: AdminMeeting}}> {
  return adminApi.put(`${API_URL}/admin/meetings/${id}`, data)
}

// Claim meeting (set who will attend)
export function claimMeeting(
  meetingId: string,
  adminId: string
): Promise<{data: {success: boolean; meeting: AdminMeeting}}> {
  return adminApi.put(`${API_URL}/admin/meetings/${meetingId}/claim`, {admin_id: adminId})
}

// Delete meeting
export function deleteMeeting(id: string): Promise<{data: {success: boolean}}> {
  return adminApi.delete(`${API_URL}/admin/meetings/${id}`)
}

// Add internal note to meeting
export function addMeetingNote(
  meetingId: string,
  text: string
): Promise<{data: {success: boolean; meeting: AdminMeeting}}> {
  return adminApi.post(`${API_URL}/admin/meetings/${meetingId}/notes`, {text})
}

// Reschedule meeting
export function rescheduleMeeting(
  meetingId: string,
  meeting_date: string
): Promise<{data: {success: boolean; meeting: AdminMeeting}}> {
  return adminApi.put(`${API_URL}/admin/meetings/${meetingId}/reschedule`, {meeting_date})
}

// Get account onboarding status (profiles + campaigns)
export function getAccountOnboardingStatus(accountId: string): Promise<{data: AccountOnboardingStatus}> {
  return adminApi.get(`${API_URL}/admin/meetings/account-status/${accountId}`)
}

// Update onboarding step note
export function updateOnboardingNote(
  accountId: string,
  step: string,
  note: string
): Promise<{data: {success: boolean; onboarding_notes: import('./_models').OnboardingNotes}}> {
  return adminApi.put(`${API_URL}/admin/meetings/account-status/${accountId}/notes`, {step, note})
}

// Login as account (switch account)
export function switchToAccount(accountId: string): Promise<{data: {success: boolean; token: string}}> {
  return adminApi.get(`${API_URL}/admin/switch-account/${accountId}`)
}

// Send WhatsApp message for a meeting
export function sendMeetingWhatsApp(
  meetingId: string,
  type: 'scheduled' | 'reminder'
): Promise<{data: {success: boolean; meeting: AdminMeeting}}> {
  return adminApi.post(`${API_URL}/admin/meetings/${meetingId}/send-whatsapp`, {type})
}

// Get WhatsApp message templates
export function getWhatsAppTemplates(): Promise<{data: {templates: import('./_models').WhatsAppTemplates}}> {
  return adminApi.get(`${API_URL}/admin/meetings/whatsapp-templates`)
}

// Update WhatsApp message templates
export function updateWhatsAppTemplates(
  templates: import('./_models').WhatsAppTemplates
): Promise<{data: {success: boolean; templates: import('./_models').WhatsAppTemplates}}> {
  return adminApi.put(`${API_URL}/admin/meetings/whatsapp-templates`, templates)
}

// Get taken time slots for an admin on a date
export function getTakenSlots(
  admin_id: string,
  date: string,
  exclude_meeting?: string
): Promise<{data: {takenSlots: {time: string; minutes: number; title: string; account: string}[]}}> {
  return adminApi.get(`${API_URL}/admin/meetings/taken-slots`, {params: {admin_id, date, exclude_meeting}})
}

// Search accounts for meeting creation.
//
// `include_expired` is on by default: a meeting is very often exactly what a
// customer whose subscription lapsed needs, and the endpoint's default query
// hides anyone past their trial_period_end, so they simply couldn't be booked.
// `searchAll` additionally drops the locked/inactive filters for the rare case
// of booking with a disabled account.
// light=1 skips the endpoint's heavy stat joins (a ~27s aggregation over 370K
// profiles / 13.6M posts) — the picker only shows name, email and phone.
export function searchAccounts(
  q: string,
  opts?: {searchAll?: boolean}
): Promise<{data: {accounts: any[]}}> {
  return adminApi.get(`${API_URL}/admin/search-accounts`, {
    params: {
      q,
      limit: 10,
      light: 1,
      ...(opts?.searchAll ? {search_all: 1} : {include_expired: 1}),
    },
  })
}

